// Sopwith 3D - title and game-over screens.
//
// A DOM overlay over the live world. The title offers the three play modes
// and shows the high score table; the game-over screen shows the final
// score, medals and ribbons, and returns to the title.

import { MEDAL_NAMES, RIBBON_NAMES } from '../sim/medals';
import { PlayMode } from '../sim/types';
import type { Score } from '../sim/types';
import type { HighScore, HighScores } from './highscores';

export interface StartChoice {
  mode: PlayMode;
}

const MODE_LABELS: [PlayMode, string, string][] = [
  [PlayMode.COMPUTER, 'Dogfight', 'Enemy planes defend their bases. The full game.'],
  [PlayMode.SINGLE, 'Solo', 'Bomb the buildings; no enemy planes.'],
  [PlayMode.NOVICE, 'Novice', 'No stalls, infinite ammo, no birds or oxen.'],
];

export class TitleScreen {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  onStart: ((choice: StartChoice) => void) | null = null;
  /** Extra line under the subtitle (custom mission name, load errors). */
  note = '';
  /** Retro (classic CGA) display toggle. */
  retro = false;
  onRetro: ((retro: boolean) => void) | null = null;
  private lastMode = PlayMode.COMPUTER;

  constructor(
    container: HTMLElement,
    private readonly highs: HighScores,
  ) {
    this.root = el('div', 'title');
    this.body = el('div', 'title-body');
    this.root.appendChild(this.body);
    container.appendChild(this.root);
    window.addEventListener('keydown', (e) => {
      if (this.root.hidden) return;
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        this.onStart?.({ mode: this.lastMode });
      } else if (e.code === 'Digit1') this.onStart?.({ mode: PlayMode.COMPUTER });
      else if (e.code === 'Digit2') this.onStart?.({ mode: PlayMode.SINGLE });
      else if (e.code === 'Digit3') this.onStart?.({ mode: PlayMode.NOVICE });
    });
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  hide(): void {
    this.root.hidden = true;
  }

  showTitle(): void {
    this.body.replaceChildren();
    const h1 = el('h1');
    h1.textContent = 'SOPWITH 3D';
    const sub = el('p', 'title-sub');
    sub.textContent = 'A remake of Sopwith (1984). Bomb the enemy bases and get home.';
    this.body.append(h1, sub);
    if (this.note) {
      const n = el('p', 'title-note');
      n.textContent = this.note;
      this.body.appendChild(n);
    }
    this.body.append(this.modeButtons(), this.retroToggle(), this.scoreTable());
    const foot = el('p', 'title-foot');
    foot.textContent =
      'Enter or click to start · X/Z throttle · ↑↓ pitch · Space gun · B bomb · H home · S sound · Esc quit';
    this.body.appendChild(foot);
    this.root.hidden = false;
  }

  showGameOver(score: Score, level: number, mode: PlayMode, rank: number): void {
    this.lastMode = mode;
    this.body.replaceChildren();
    const h1 = el('h1');
    h1.textContent = 'GAME OVER';
    const s = el('p', 'title-score');
    s.textContent = `Score ${score.score} · reached level ${level + 1}`;
    if (rank > 0) {
      const r = el('p', 'title-rank');
      r.textContent = rank === 1 ? 'New high score!' : `High score #${rank}`;
      this.body.append(h1, s, r);
    } else {
      this.body.append(h1, s);
    }
    const awards = el('div', 'title-awards');
    for (const m of score.medals) awards.appendChild(award('🎖', MEDAL_NAMES[m]));
    for (const r of score.ribbons) awards.appendChild(award('🎗', RIBBON_NAMES[r]));
    if (!awards.childElementCount) {
      awards.textContent = 'No decorations this time.';
    }
    this.body.append(awards, this.modeButtons('Fly again'), this.retroToggle(), this.scoreTable());
    this.root.hidden = false;
  }

  private retroToggle(): HTMLElement {
    const label = document.createElement('label');
    label.className = 'title-retro';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = this.retro;
    box.addEventListener('change', () => {
      this.retro = box.checked;
      this.onRetro?.(this.retro);
    });
    const text = el('span');
    text.textContent = 'Retro mode: the original 320x200 four-colour look (R in game)';
    label.append(box, text);
    return label;
  }

  private modeButtons(prefix = 'Play'): HTMLElement {
    const box = el('div', 'title-modes');
    MODE_LABELS.forEach(([mode, label, desc], i) => {
      const b = document.createElement('button');
      b.className = 'title-mode';
      const k = el('span', 'title-key');
      k.textContent = String(i + 1);
      const l = el('span', 'title-label');
      l.textContent = `${prefix}: ${label}`;
      const d = el('span', 'title-desc');
      d.textContent = desc;
      b.append(k, l, d);
      b.addEventListener('click', () => {
        this.lastMode = mode;
        this.onStart?.({ mode });
      });
      box.appendChild(b);
    });
    return box;
  }

  private scoreTable(): HTMLElement {
    const box = el('div', 'title-scores');
    const h = el('h2');
    h.textContent = 'High scores';
    box.appendChild(h);
    const list = this.highs.list;
    if (!list.length) {
      const p = el('p');
      p.textContent = 'No scores yet.';
      box.appendChild(p);
      return box;
    }
    const table = document.createElement('table');
    list.forEach((e: HighScore, i) => {
      const tr = document.createElement('tr');
      for (const text of [
        `${i + 1}.`,
        String(e.score),
        `L${e.level + 1}`,
        e.mode,
        '🎖'.repeat(Math.min(3, e.medals)),
      ]) {
        const td = document.createElement('td');
        td.textContent = text;
        tr.appendChild(td);
      }
      table.appendChild(tr);
    });
    box.appendChild(table);
    return box;
  }
}

function award(icon: string, name: string): HTMLElement {
  const a = el('span', 'title-award');
  a.textContent = `${icon} ${name}`;
  return a;
}

function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}
